import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { fmtDate, fmtMoney, fmtNumber } from '../lib/format';
import { Button, Callout, Empty, ErrorState, Ltr, useToast } from '../components/ui';
import { DeliveryDialog } from '../components/DeliveryProof';

type PickLine = { key: string; sku: string | null; name: string; variant: string | null; text: string | null; quantity: number; unit: string | null; packInfo: string | null; inCatalog: boolean };
type PickList = {
  kind: 'order' | 'document';
  id: number;
  title: string;
  customer: { id: number; name: string; phone: string | null; contact: string | null } | null;
  address: { text: string; contact: string | null; phone: string | null; notes: string | null } | null;
  note: string | null;
  remarks: string | null;
  total: number | null;
  orderId: number | null;
  documentId: number | null;
  deliveredAt: string | null;
  lines: PickLine[];
  picked: string[];
  completedAt: string | null;
  updatedAt: string | null;
};

const qtyText = (n: number) => (Number.isInteger(n) ? String(n) : n.toLocaleString('he-IL', { maximumFractionDigits: 2 }));

/**
 * The picking screen: one order, every line big and clear, tick as you pick. Made for a phone in
 * the warehouse as much as for the computer; progress is saved and refreshed, so two people (or
 * two devices) see the same ticks.
 */
export function PickPage() {
  const params = useParams();
  const kind = params.kind === 'document' ? 'document' : 'order';
  const id = Number(params.id);
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const key = ['pick', kind, id];
  const { data, error, refetch } = useQuery({
    queryKey: key,
    queryFn: () => api<PickList | { redirect: { kind: 'order'; id: number } }>(`/api/pick/${kind}/${id}`),
    refetchInterval: 15_000,
  });
  const [busy, setBusy] = useState(false);
  const [delivering, setDelivering] = useState(false);
  // A Morning document that came from an order: pick the order (it has the catalog lines).
  useEffect(() => {
    if (data && 'redirect' in data) navigate(`/pick/${data.redirect.kind}/${data.redirect.id}`, { replace: true });
  }, [data]);

  if (error) return <div className="pick-page"><ErrorState error={error} onRetry={() => refetch()} /></div>;
  if (!data || 'redirect' in data) return <div className="pick-page"><span className="spinner" /></div>;
  const list = data;
  const picked = new Set(list.picked);
  const done = list.lines.length > 0 && picked.size === list.lines.length;

  const toggle = async (line: PickLine) => {
    const next = !picked.has(line.key);
    // Instant on screen; the server confirms.
    qc.setQueryData(key, { ...list, picked: next ? [...list.picked, line.key] : list.picked.filter((k) => k !== line.key) });
    try {
      await api(`/api/pick/${kind}/${id}`, { body: { key: line.key, picked: next } });
      qc.invalidateQueries({ queryKey: ['deliveries'] });
    } catch (e) {
      toast.error(e);
      refetch();
    }
  };
  const setAll = async (all: boolean) => {
    setBusy(true);
    try {
      await api(`/api/pick/${kind}/${id}`, { body: { all } });
      await refetch();
      qc.invalidateQueries({ queryKey: ['deliveries'] });
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const delivered = async () => {
    setBusy(true);
    try {
      await api(`/api/pick/${kind}/${id}/delivered`, { body: { delivered: true } });
      qc.invalidateQueries({ queryKey: ['deliveries'] });
      toast.success('ההזמנה סומנה כסופקה');
      await refetch();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const share = async () => {
    const url = window.location.href;
    const text = `ליקוט: ${list.customer?.name ?? ''} — ${list.title}\n${url}`;
    if (navigator.share) await navigator.share({ title: 'ליקוט הזמנה', text, url }).catch(() => undefined);
    else window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  };
  const pct = list.lines.length ? Math.round((picked.size / list.lines.length) * 100) : 0;
  const maps = list.address?.text ? `https://waze.com/ul?q=${encodeURIComponent(list.address.text)}` : null;

  return (
    <div className="pick-page">
      <div className="pick-top no-print">
        <Link to="/pick" className="btn sm ghost">→ כל ההזמנות</Link>
        <span className="spacer" />
        <Button size="sm" variant="ghost" onClick={share}>שליחה לטלפון</Button>
        <Button size="sm" variant="ghost" onClick={() => window.print()}>הדפסה</Button>
      </div>

      <header className="pick-head">
        <div className="pick-customer">{list.customer?.name ?? 'לקוח'}</div>
        <div className="pick-title">{list.title} · {fmtNumber(list.lines.length)} שורות{list.total != null ? ` · ${fmtMoney(list.total)}` : ''}</div>
        {list.address?.text ? (
          <div className="pick-meta">
            📍 {maps ? <a href={maps} target="_blank" rel="noreferrer noopener">{list.address.text}</a> : list.address.text}
            {list.address.contact ? ` · ${list.address.contact}` : ''}
            {list.address.notes ? <div className="small muted">{list.address.notes}</div> : null}
          </div>
        ) : null}
        {list.customer?.phone || list.address?.phone ? (
          <div className="pick-meta">📞 <a href={`tel:${list.address?.phone || list.customer?.phone}`}><Ltr>{list.address?.phone || list.customer?.phone}</Ltr></a>{list.customer?.contact ? ` · ${list.customer.contact}` : ''}</div>
        ) : null}
        {list.note ? <Callout tone="warn">{list.note}</Callout> : null}
        {list.remarks ? <div className="pick-meta small">הערות במסמך: {list.remarks}</div> : null}
      </header>

      <div className="pick-progress no-print" aria-label={`לוקטו ${picked.size} מתוך ${list.lines.length}`}>
        <div className="row">
          <strong>{done ? '✓ הכל לוקט' : `לוקטו ${fmtNumber(picked.size)} מתוך ${fmtNumber(list.lines.length)}`}</strong>
          <span className="spacer" />
          {done ? (
            <Button size="sm" variant="ghost" onClick={() => setAll(false)} loading={busy}>איפוס</Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setAll(true)} loading={busy}>סימון הכל</Button>
          )}
        </div>
        <div className="progress" style={{ marginTop: 6 }}><span style={{ width: `${pct}%` }} /></div>
      </div>

      {list.lines.length === 0 ? (
        <Empty title="אין שורות בהזמנה" />
      ) : (
        <ol className="pick-lines">
          {list.lines.map((l) => {
            const on = picked.has(l.key);
            return (
              <li key={l.key}>
                <button type="button" className={`pick-line ${on ? 'picked' : ''}`} onClick={() => toggle(l)} aria-pressed={on}>
                  <span className="pick-check" aria-hidden>{on ? '✓' : ''}</span>
                  <span className="pick-qty">
                    {qtyText(l.quantity)}
                    {l.unit ? <small>{l.unit}</small> : null}
                  </span>
                  <span className="pick-body">
                    <span className="pick-name">{l.name}</span>
                    {l.variant ? <span className="pick-variant">{l.variant}</span> : null}
                    <span className="pick-sub">
                      {l.sku ? <Ltr className="tabular">{l.sku}</Ltr> : <span>לא מהקטלוג</span>}
                      {l.packInfo ? ` · ${l.packInfo}` : ''}
                      {l.text ? ` · במסמך: ${l.text}` : ''}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      )}

      <div className="pick-bottom no-print">
        {list.deliveredAt ? (
          <>
            <Callout tone="ok">סופק {fmtDate(list.deliveredAt)}</Callout>
            {list.documentId ? <Link to={`/delivery/${list.documentId}`} className="btn sm">אישור המסירה</Link> : null}
          </>
        ) : list.documentId ? (
          <>
            <Button variant="primary" onClick={() => setDelivering(true)} title={done ? undefined : 'לא כל השורות סומנו כלוקטו'}>
              ✍ מסירה ללקוח
            </Button>
            <Button size="sm" variant="ghost" onClick={delivered} loading={busy} disabled={!done} title={done ? 'בלי חתימה' : 'אחרי שהכל לוקט'}>
              סופק בלי חתימה
            </Button>
          </>
        ) : (
          <span className="small muted">עוד לא הופק מסמך להזמנה — אחרי ההפקה אפשר לסמן כאן "סופק".</span>
        )}
        {list.orderId ? <Link to={`/orders/${list.orderId}`} className="btn ghost sm">פתיחת ההזמנה בעורך</Link> : null}
      </div>
      {delivering ? (
        <DeliveryDialog
          kind={kind}
          id={id}
          customerName={list.customer?.name ?? 'לקוח'}
          contact={null}
          onClose={() => setDelivering(false)}
          onDone={() => {
            setDelivering(false);
            qc.invalidateQueries({ queryKey: ['deliveries'] });
            refetch();
            navigate(`/delivery/${list.documentId}`);
          }}
        />
      ) : null}
    </div>
  );
}

type OpenOrders = {
  preparing: Array<{ orderId: number; typeLabel: string; customerName: string; lines: number; prepNote: string | null; daysOpen: number; pick: { picked: number; lines: number; completed: boolean } }>;
  items: Array<{ documentId: number; orderId: number | null; typeLabel: string; number: string | null; customerName: string; lines: number; address: string | null; issueDate: string; pick: { picked: number; lines: number; completed: boolean } }>;
};

/** All orders open for picking — the starting point on a phone. */
export function PickListPage() {
  const { data, error, refetch } = useQuery({ queryKey: ['deliveries', 'pick-list'], queryFn: () => api<OpenOrders>('/api/pick/open'), refetchInterval: 30_000 });
  if (error) return <div className="panel"><ErrorState error={error} onRetry={() => refetch()} /></div>;
  const rows = data
    ? [
        ...data.preparing.map((p) => ({ to: `/pick/order/${p.orderId}`, name: p.customerName, what: `טיוטת ${p.typeLabel} · בהכנה`, note: p.prepNote, pick: p.pick })),
        ...data.items.map((d) => ({ to: d.orderId ? `/pick/order/${d.orderId}` : `/pick/document/${d.documentId}`, name: d.customerName, what: `${d.typeLabel} ${d.number ?? ''} · ${fmtDate(d.issueDate)}`, note: d.address, pick: d.pick })),
      ]
    : [];
  return (
    <>
      <div className="page-head">
        <div>
          <h1>ליקוט</h1>
          <div className="sub">ההזמנות הפתוחות — לוחצים על הזמנה ומסמנים כל מוצר שלוקט. עובד גם בטלפון.</div>
        </div>
      </div>
      <section className="panel">
        {!data ? (
          <div className="panel-body"><span className="spinner" /></div>
        ) : rows.length === 0 ? (
          <Empty title="אין הזמנות פתוחות לליקוט" />
        ) : (
          <ul className="pick-index">
            {rows.map((r) => (
              <li key={r.to}>
                <Link to={r.to} className="pick-index-row">
                  <span className="pick-index-main">
                    <strong>{r.name}</strong>
                    <span className="small muted">{r.what}</span>
                    {r.note ? <span className="small" style={{ color: 'var(--warn)' }}>{r.note}</span> : null}
                  </span>
                  <span className={`badge ${r.pick.completed ? 'ok' : r.pick.picked ? 'warn' : ''}`}>
                    {r.pick.completed ? '✓ לוקט' : r.pick.picked ? `${r.pick.picked}/${r.pick.lines}` : `${r.pick.lines} שורות`}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
